import React, { useState, useEffect } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import { Button, Modal, Form, Row, Col } from "react-bootstrap";
import axios from "axios";
import { updateMedium } from "../../store/LibrarySlice";

const LibraryHeader = ({
  bulkSelectMode,
  onBulkSelect,
  onPlaySelected,
  onDeleteSelected,
  onCancelSelected,
  onEditSelected,
  selectedPhotos = [],
  libraryItems,
  dispatch,
}) => {
  // Modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editValues, setEditValues] = useState({
    rotate: 0,
    red: 0,
    green: 0,
    blue: 0,
    saturation: 1,
    crf: 23,
    t: 10
  });
  
  
  // Store thumbnail URLs for selected photos
  const [thumbnails, setThumbnails] = useState([]);
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);
//   const currentMediaId = selectedPhotos[currentPreviewIndex]?.mediaId;
// const editValues = editValuesByMediaId[currentMediaId] || {};

  
  // Fetch thumbnails when modal opens
  useEffect(() => {
    if (showEditModal && selectedPhotos.length > 0) {
      const fetchThumbnails = async () => {
        const thumbnailUrls = [];
        for (const photo of selectedPhotos) {
          try {
            const response = await axios.get(`/api/medium/thumbnail/media/${photo.mediaId}`);
            thumbnailUrls.push({
              mediaId: photo.mediaId,
              url: response.data
            });
          } catch (error) {
            console.error(`Failed to fetch thumbnail for ${photo.mediaId}`, error);
          }
        }
        setThumbnails(thumbnailUrls);
      };
      
      fetchThumbnails();
    }
  }, [showEditModal, selectedPhotos]);

  const handleBulkSelectOn = (ev) => {
    onBulkSelect && onBulkSelect();
  };

  const handleDeleteSelected = (ev) => {
    onDeleteSelected && onDeleteSelected();
  };
  
  const handlePlaySelected = (ev) => {
    onPlaySelected && onPlaySelected();
  };

  const handleCancelSelected = (ev) => {
    onCancelSelected && onCancelSelected();
  };

  const handleEditClick = () => {
    if (selectedPhotos.length > 0) {
      const firstPhoto = selectedPhotos[0];
      setEditValues({
        rotate: typeof firstPhoto.rotate === 'number' ? firstPhoto.rotate : 0,
        red: firstPhoto.red || 0,
        green: firstPhoto.green || 0,
        blue: firstPhoto.blue || 0,
        saturation: firstPhoto.saturation || 1,
        crf: firstPhoto.crf || 23,
        t: firstPhoto.t || 10
      });
      setCurrentPreviewIndex(0);
      setShowEditModal(true);
    }
  };
  

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditValues((prev) => ({ ...prev, [name]: parseFloat(value) }));
  };
  

  const handleEditSave = async () => {
    onEditSelected(editValues);
    try {
      for (const photo of selectedPhotos) {
        await axios.post("/api/media/save-edit", {
          mediaId: photo.mediaId,
          ...editValues,
          year: photo.year,
          month: photo.month,
          type: photo.type,
          path: photo.path,
          size: photo.size
        });
      }
      console.log("Backend update successful");
    } catch (error) {
      console.error("Failed to update backend:", error);
    }
    setShowEditModal(false);
  };
  
  

  const handleEditModalClose = () => {
    setShowEditModal(false);
  };
  
  const handleNextPreview = () => {
    setCurrentPreviewIndex((prev) => 
      prev < selectedPhotos.length - 1 ? prev + 1 : 0
    );
  };
  
  const handlePrevPreview = () => {
    setCurrentPreviewIndex((prev) => 
      prev > 0 ? prev - 1 : selectedPhotos.length - 1
    );
  };
  
  // RGB values need to be scaled for CSS filter
  const getRgbFilterStyle = () => {
    // Approximation of RGB effect using CSS filters
    // Better in a real application to use canvas or WebGL for accurate color manipulation
    const redFactor = editValues.red / 100;
    const greenFactor = editValues.green / 100;
    const blueFactor = editValues.blue / 100;
    
    return {
      filter: `
        saturate(${editValues.saturation})
        brightness(${Math.max(redFactor, greenFactor, blueFactor, 1)})
      `,
      backgroundColor: `rgba(${editValues.red}, ${editValues.green}, ${editValues.blue}, 0.2)`,
      mixBlendMode: "color"
    };
  };

  return (
    <div className="d-flex justify-content-between">
      <div>
        {bulkSelectMode === false && (
          <div className="d-flex flex-row">
            <div className="p-2">
              <Button onClick={handleBulkSelectOn}>Bulk Select</Button>
            </div>
          </div>
        )}
        {bulkSelectMode === true && (
          <Button variant="danger" onClick={handleDeleteSelected}>
            Delete permanently
          </Button>
        )}
      </div>
      <div>
        {bulkSelectMode === true && (
          <>
            <Button variant="primary" className="me-2" onClick={handleEditClick}>
              Edit Selected ({selectedPhotos.length})
            </Button>
            <Button variant="success" onClick={handlePlaySelected}>
              Play Selected
            </Button>
          </>
        )}
        <Modal show={showEditModal} onHide={handleEditModalClose} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              Edit Photos ({selectedPhotos.length} selected)
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col md={7}>
                <Form>
                  {/* Rotate */}
                  <Form.Group className="mb-3">
                    <Form.Label>Rotate</Form.Label>
                    <div>
                      <Button
                        variant="secondary"
                        onClick={() => setEditValues({ ...editValues, rotate: (editValues.rotate - 90 + 360) % 360 })}
                        className="me-2"
                      >
                        Left
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => setEditValues({ ...editValues, rotate: (editValues.rotate + 90) % 360 })}
                      >
                        Right
                      </Button>
                      <span className="ms-3">{editValues.rotate}°</span>
                    </div>
                  </Form.Group>

                  {/* RGB */}
                  <Form.Group className="mb-3">
                    <Form.Label>RGB</Form.Label>
                    <div className="d-flex">
                      <div className="me-2">
                        <Form.Label>Red</Form.Label>
                        <Form.Control
                          type="range"
                          min={0}
                          max={255}
                          name="red"
                          value={editValues.red}
                          onChange={handleEditChange}
                        />
                        <div className="text-center">{editValues.red}</div>
                      </div>
                      <div className="me-2">
                        <Form.Label>Green</Form.Label>
                        <Form.Control
                          type="range"
                          min={0}
                          max={255}
                          name="green"
                          value={editValues.green}
                          onChange={handleEditChange}
                        />
                        <div className="text-center">{editValues.green}</div>
                      </div>
                      <div>
                        <Form.Label>Blue</Form.Label>
                        <Form.Control
                          type="range"
                          min={0}
                          max={255}
                          name="blue"
                          value={editValues.blue}
                          onChange={handleEditChange}
                        />
                        <div className="text-center">{editValues.blue}</div>
                      </div>
                    </div>
                  </Form.Group>

                  {/* Saturation */}
                  <Form.Group className="mb-3">
                    <Form.Label>Saturation ({editValues.saturation})</Form.Label>
                    <Form.Control
                      type="range"
                      min={0}
                      max={10}
                      step={0.1}
                      name="saturation"
                      value={editValues.saturation}
                      onChange={handleEditChange}
                    />
                  </Form.Group>

                  {/* CRF */}
                  <Form.Group className="mb-3">
                    <Form.Label>CRF (Compression) ({editValues.crf})</Form.Label>
                    <Form.Control
                      type="range"
                      min={0}
                      max={52}
                      name="crf"
                      value={editValues.crf}
                      onChange={handleEditChange}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Duration (seconds) ({editValues.t})</Form.Label>
                    <Form.Control
                      type="range" // Changed to range
                      min={1}
                      max={600}
                      step={1} // Added step for whole seconds
                      name="t"
                      value={editValues.t}
                      onChange={handleEditChange}
                    />
                  </Form.Group>
                </Form>
              </Col>
              
              {/* Preview Section */}
              <Col md={5}>
                <div className="d-flex flex-column align-items-center">
                  <h5>Preview ({currentPreviewIndex + 1}/{selectedPhotos.length})</h5>
                  
                  {thumbnails.length > 0 && (
                    <div className="position-relative mt-2 mb-3" style={{ width: "100%", height: "250px" }}>
                      <div className="position-absolute" style={{ 
                        top: 0, 
                        left: 0, 
                        width: "100%", 
                        height: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        overflow: "hidden"
                      }}>
                        <div style={{ 
                          position: "relative",
                          width: "100%", 
                          height: "100%",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center"
                        }}>
                          <img
                            src={thumbnails[currentPreviewIndex]?.url}
                            alt="Preview"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "100%",
                              objectFit: "contain",
                              transform: `rotate(${editValues.rotate}deg)`,
                            }}
                          />
                          <div className="position-absolute" style={{
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            ...getRgbFilterStyle()
                          }}></div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {selectedPhotos.length > 1 && (
                    <div className="d-flex justify-content-between w-100 mt-2">
                      <Button variant="secondary" onClick={handlePrevPreview}>
                        Previous
                      </Button>
                      <Button variant="secondary" onClick={handleNextPreview}>
                        Next
                      </Button>
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleEditModalClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleEditSave}>
              Save Changes to All Selected
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default LibraryHeader;